/**
 * @file api.ts
 * @brief Inbox data for web and desktop: unread count, notifications, what is coming up, and where each opens.
 *
 * @details
 * Web/desktop port of the phone's Inbox (DES 12-10-2026; FRONTEND
 * src/features/inbox). Same endpoints:
 *  - GET  /api/push/inbox?before=N  notifications of the last 30 days
 *  - GET  /api/push/inbox/unread    badge count
 *  - POST /api/push/inbox/read      { seq } → everything up to it is read
 * and the same upcoming list the desktop alarms use (fetchScheduledOverview).
 *
 * The server's `url` is the phone's deep link; on the web a notification's
 * `target` maps to a page here (`webPathFor`), the same mapping the desktop
 * app's main process uses for a clicked notification (DESKTOP
 * src/notifications.js pathFor).
 *
 * React Query keys are cleared on sign-out (AuthContext queryClient.clear()),
 * so one user's inbox never shows to the next.
 *
 * @author DES
 * @date 12-10-2026
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/shared/api/client";
import { useAuth } from "@/shared/auth/auth-context";
import { isCompanyAdminRole } from "@/shared/auth/homePath";
import { fetchScheduledOverview } from "@/features/scheduleReminders/desktopScheduleSync";

import type { InboxTarget } from "./inboxPaths";

export { webPathFor, type InboxTarget } from "./inboxPaths";

export type InboxUpdate = {
  seq: string;
  kind: string;
  category: string;
  title: string;
  body: string;
  icon: string | null;
  target: InboxTarget | null;
  url: string;
  released_at: string;
  unread: boolean;
};

type InboxPage = {
  items: InboxUpdate[];
  unread: number;
  readSeq: string;
  nextBefore: string | null;
};

const SOON_MS = 24 * 60 * 60 * 1000;
const POLL_MS = 60_000;

export const inboxKeys = {
  all: ["inbox"] as const,
  unread: ["inbox", "unread"] as const,
  updates: ["inbox", "updates"] as const,
  upcoming: (base: string) => ["inbox", "upcoming", base] as const,
};

/**
 * "/company" for a company admin, "/admin" for a super admin (their own Inbox
 * since they got a Home, DES 13-10-2026), "/app" for everyone else.
 */
export function useInboxBase(): { base: "/admin" | "/app" | "/company"; enabled: boolean } {
  const { user, status } = useAuth();
  const enabled = status === "authenticated" && !!user;
  const base =
    user?.role === "superadmin" ? "/admin" : user && isCompanyAdminRole(user.role) ? "/company" : "/app";
  return { base, enabled };
}

export function useInboxUnread() {
  const { enabled } = useInboxBase();
  return useQuery({
    queryKey: inboxKeys.unread,
    queryFn: () => apiGet<{ unread: number }>("/api/push/inbox/unread").then((r) => Number(r?.unread) || 0),
    enabled,
    staleTime: POLL_MS,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });
}

export function useInboxUpcoming() {
  const { base, enabled } = useInboxBase();
  const paths = {
    meeting: `${base}/meetings/scheduled`,
    session: base === "/app" ? "/app" : `${base}/home`,
  };
  return useQuery({
    queryKey: inboxKeys.upcoming(base),
    queryFn: () => fetchScheduledOverview(paths),
    enabled,
    staleTime: POLL_MS,
    refetchInterval: POLL_MS,
  });
}

/** True when a meeting or session starts within 24 h (the badge's dot). */
export function startsSoon(items: { startMs: number }[] | undefined): boolean {
  const now = Date.now();
  return (items ?? []).some((i) => i.startMs - now < SOON_MS);
}

export function useInboxUpdates() {
  const { enabled } = useInboxBase();
  return useInfiniteQuery({
    queryKey: inboxKeys.updates,
    queryFn: ({ pageParam }) =>
      apiGet<InboxPage>("/api/push/inbox", { params: pageParam ? { before: pageParam } : undefined }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last?.nextBefore ?? null,
    enabled,
    // Every visit loads fresh, so last visit's "new" rows are read by now.
    staleTime: 0,
    refetchOnMount: "always",
  });
}

/** Everything up to `seq` is read; the badge refreshes from the answer. */
export function useMarkInboxRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (seq: string) => apiPost<{ unread: number }, { seq: string }>("/api/push/inbox/read", { seq }),
    onSuccess: (r) => {
      qc.setQueryData(inboxKeys.unread, Number(r?.unread) || 0);
    },
  });
}
